import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SESSION_TOKEN_KEY = 'pokeclass_session_token';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  // Global hard timeout: a hanging request is aborted instead of piling
  // up in the background (root cause of the infinite-loading loop)
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ─────────────────────────────────────────────────────────────────────
// Token persistence (LocalStorage): once logged in, every request sends
// the session token via Authorization header. This makes auth immune to
// third-party cookie blocking on school networks/browsers.
// ─────────────────────────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) { /* localStorage unavailable */ }
  return config;
});

// ─────────────────────────────────────────────────────────────────────
// Silent retry interceptor (silent exponential backoff: 1s → 3s → 5s)
// Reissues idempotent reads (GET/HEAD) up to 3 times on transient errors
// (network drop, 429/502/503/504) before surfacing to the UI. POST/PUT/DELETE
// are not retried automatically to avoid duplicate side effects.
// ─────────────────────────────────────────────────────────────────────
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504, 520]);
const MAX_RETRIES = 3;
const BACKOFF_DELAYS_MS = [1000, 3000, 5000];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (!config || config.__retryDisabled) return Promise.reject(error);

    const method = (config.method || 'get').toLowerCase();
    const isIdempotent = method === 'get' || method === 'head';
    if (!isIdempotent) return Promise.reject(error);

    const status = error?.response?.status;
    const isNetworkError = !error?.response;
    const isTransient = isNetworkError || TRANSIENT_STATUSES.has(status);
    if (!isTransient) return Promise.reject(error);

    config.__retryCount = (config.__retryCount || 0) + 1;
    if (config.__retryCount > MAX_RETRIES) return Promise.reject(error);

    const delay = BACKOFF_DELAYS_MS[config.__retryCount - 1];
    await new Promise((resolve) => setTimeout(resolve, delay));
    return api(config);
  }
);

export const authAPI = {
  // Login handshake: short hard timeout — abort instead of hanging
  exchangeSession: (sessionId) => api.post('/auth/session', {}, {
    headers: { 'X-Session-ID': sessionId },
    timeout: 8000
  }),
  me: () => api.get('/auth/me', { timeout: 5000 }),
  setRole: (role) => api.post(`/auth/set-role?role=${role}`),
  logout: () => api.post('/auth/logout')
};

export const classroomAPI = {
  create: (data) => api.post('/classrooms', data),
  getAll: () => api.get('/classrooms'),
  getById: (id) => api.get(`/classrooms/${id}`),
  update: (id, data) => api.put(`/classrooms/${id}`, data),
  delete: (id) => api.delete(`/classrooms/${id}`),
  getStudents: (id) => api.get(`/classrooms/${id}/students`),
  getTasks: (id) => api.get(`/classrooms/${id}/tasks`),
  createTask: (id, data) => api.post(`/classrooms/${id}/tasks`, data),
  getMissions: (id) => api.get(`/classrooms/${id}/missions`),
  createMission: (id, data) => api.post(`/classrooms/${id}/missions`, data),
  deleteMission: (classroomId, missionId) => api.delete(`/classrooms/${classroomId}/missions/${missionId}`),
  cloneMission: (missionId, targetClassroomIds) => api.post(`/missions/${missionId}/clone`, { target_classroom_ids: targetClassroomIds }),
  join: (accessCode) => api.post('/classrooms/join', { access_code: accessCode })
};

export const studentAPI = {
  getMe: () => api.get('/student/me'),
  getMonster: () => api.get('/student/monster'),
  getTasks: () => api.get('/student/tasks'),
  completeTask: (taskId) => api.post(`/student/tasks/${taskId}/complete`),
  getMissions: () => api.get('/student/missions'),
  submitMission: (missionId, answers, timeSeconds, tabSwitches) => api.post(`/student/missions/${missionId}/submit`, { answers, time_seconds: timeSeconds || 0, tab_switches: tabSwitches || 0 }),
  applyPenalty: (studentId, hpReduction) => api.post(`/students/${studentId}/penalty`, { hp_reduction: hpReduction }),
  healStudent: (studentId, hpAmount) => api.post(`/students/${studentId}/heal`, { hp_amount: hpAmount }),
  rechargeEnergy: (studentId, energyAmount) => api.post(`/students/${studentId}/recharge`, { energy_amount: energyAmount }),
  adjustXP: (studentId, xpChange) => api.post(`/students/${studentId}/adjust-xp`, { xp_change: xpChange }),
  assignClass: (studentId, assignedClass) => api.post(`/students/${studentId}/assign-class`, { assigned_class: assignedClass }),
  hatchEgg: (studentId) => api.post(`/students/${studentId}/hatch-egg`),
  evolveMonster: (studentId) => api.post(`/students/${studentId}/evolve`),
  getNotifications: () => api.get('/student/notifications'),
  markNotificationRead: (notificationId) => api.post(`/student/notifications/${notificationId}/read`),
  markAllNotificationsRead: () => api.post('/student/notifications/mark-all-read'),
  getEvolutionHistory: () => api.get('/student/evolution-history'),
  resetEgg: (studentId) => api.post(`/students/${studentId}/reset-egg`),
  changePokemon: (studentId, monsterIndex) => api.post(`/students/${studentId}/change-pokemon`, { monster_index: monsterIndex }),
  renameMonster: (studentId, name) => api.post(`/students/${studentId}/rename-monster`, { name }),
  removeStudent: (studentId) => api.delete(`/students/${studentId}/remove`),
  // Win Streak & Mochila endpoints
  getPendingCount: () => api.get('/student/pending-count'),
  getWinStreak: () => api.get('/student/win-streak'),
  // Inventory endpoints
  getInventory: () => api.get('/student/inventory'),
  markInventorySeen: () => api.post('/student/inventory/mark-seen'),
  useItem: (inventoryId) => api.post('/student/inventory/use', { inventory_id: inventoryId }),
  // Clears the server-side flag that triggers the evolution animation.
  dismissEvolutionNotice: () => api.post('/student/dismiss-evolution-notice')
};

export const gifAPI = {
  getByClass: (classType) => api.get(`/gifs/class/${classType}`),
  getConfig: () => api.get('/admin/gifs/config')
};

export const powerAPI = {
  getTemplates: () => api.get('/admin/powers'),
  updateTemplate: (classType, level, data) => api.post(`/admin/powers/${classType}/${level}`, data),
  deleteTemplate: (classType, level) => api.delete(`/admin/powers/${classType}/${level}`),
  getPublicConfig: () => api.get('/admin/powers/config'),
  usePower: (powerId) => api.post('/student/use-power', { power_id: powerId }),
  useCooperativePower: (powerId, targetStudentIds, targetWholeGroup) => api.post('/student/use-cooperative-power', {
    power_id: powerId,
    target_student_ids: targetStudentIds || [],
    target_whole_group: targetWholeGroup || false
  }),
  getGroupMembers: () => api.get('/student/group-members'),
  getSupportHistory: () => api.get('/student/support-history'),
  getGroupStatus: () => api.get('/student/group-status')
};

export const shopAPI = {
  getCatalog: () => api.get('/shop/catalog'),
  purchase: (itemType) => api.post('/shop/purchase', { item_type: itemType }),
  updateConfig: (classroomId, stockOverrides) => api.post(`/shop/config/${classroomId}`, { stock_overrides: stockOverrides })
};

export const behaviorAPI = {
  getTemplates: () => api.get('/admin/behaviors'),
  updateTemplate: (behaviorType, position, data) => api.post(`/admin/behaviors/${behaviorType}/${position}`, data),
  deleteTemplate: (behaviorType, position) => api.delete(`/admin/behaviors/${behaviorType}/${position}`),
  applyBehavior: (studentId, behaviorId) => api.post(`/students/${studentId}/apply-behavior`, { behavior_id: behaviorId })
};

export const missionAPI = {
  generateWithAI: (params) => api.post('/missions/generate-ai', params)
};

export const teacherNotificationAPI = {
  getNotifications: (classroomId) => api.get('/teacher/notifications', { params: classroomId ? { classroom_id: classroomId } : {} }),
  getUnreadCount: (classroomId) => api.get('/teacher/notifications/unread-count', { params: classroomId ? { classroom_id: classroomId } : {} }),
  markAsRead: (notificationId) => api.post(`/teacher/notifications/${notificationId}/read`),
  markAllAsRead: (classroomId) => api.post('/teacher/notifications/mark-all-read', classroomId ? { classroom_id: classroomId } : {})
};

export const adminUsersAPI = {
  listAll: () => api.get('/admin/all-users')
};

export const adminPanelAPI = {
  listUsers:    ()                => api.get('/admin-panel/users'),
  listTeachers: ()                => api.get('/admin-panel/teachers'),
  toggleBlock:  (userId, block, reason) => api.post(`/admin-panel/users/${userId}/block`, { block, reason }),
  deleteUser:   (userId)          => api.delete(`/admin-panel/users/${userId}`),
};

export default api;
